import { Widget, ViewWidgetHelper, ViewCmdType, ImSys, getMainValue, DomHelpers, setMainValue, utils } from '@nordbox/core';

// widgets/NFSplitProjectWidget.ts
/**
 * NFSplitProjectWidget — перенос jQuery-виджета `nfSplitProject` на чистый TS.
 * Логика 1:1 с легаси. Без добавления новых фич.
 *
 * Важное отличие от jQuery-версии:
 * - вместо .off().on() используется единое делегирование событий на root,
 *   чтобы исключить накопление обработчиков при повторных init/reload.
 */
class NFSplitProjectWidget extends Widget {
    constructor() {
        super(...arguments);
        /** fps видео (читается при _create). */
        this._fps = 25;
        /** всего кадров (читается при _create). */
        this._totalFrames = 0;
        /** Буферизованное сохранение: выполняется ли сейчас. */
        this._buffSaveRunning = false;
        /** Буферизованное сохранение: требуется ли ещё один прогон. */
        this._buffSaveRequired = false;
        /** Буферизованное сохранение: команда в процессе. */
        this._buffCmdExecuting = false;
        /** Признак, что обработчики уже навешаны на root. */
        this._handlersBound = false;
    }
    static _cssKey() { return "nf-splitproject"; }
    /** Точка входа виджета. */
    _create(_options) {
        const root = this.element;
        // FPS / суммарные кадры
        const fpsEl = root.querySelector('[data-key="FPS"]');
        const tfEl = root.querySelector('[data-key="TotalFrames"]');
        this._fps = fpsEl ? parseFloat(fpsEl.value) : this._fps;
        this._totalFrames = tfEl ? parseFloat(tfEl.value) : this._totalFrames;
        // Легаси добавлял option "Создать сюжет" и выставлял 0
        this._initAttachmentSelect(root);
        // Единые делегированные обработчики (аналог .off().on() по смыслу)
        this._bindHandlersOnce(root);
        // Режим read-only (как в легаси: .off() + class off)
        this._applyReadOnlyState(root);
    }
    /** Инициализация select[data-key=AttachmentID] (как в легаси _create). */
    _initAttachmentSelect(root) {
        const attach = root.querySelector('select[data-key="AttachmentID"]');
        if (!attach)
            return;
        // Не дублируем option при повторной инициализации
        const hasZero = Array.from(attach.options).some(o => o.value === "0" && o.textContent?.trim() === "Создать сюжет");
        if (!hasZero) {
            const opt = document.createElement("option");
            opt.value = "0";
            opt.textContent = "Создать сюжет";
            attach.append(opt);
        }
        attach.value = "0";
        // 2026-03-26
        // В legacy после val(0) не вызывали change-обработчик вручную.
        // Переключение блоков выполняется только при реальном change пользователя.
    }
    /** Делегированные обработчики — навешиваем один раз. */
    _bindHandlersOnce(root) {
        if (this._handlersBound)
            return;
        this._handlersBound = true;
        // click
        root.addEventListener("click", (ev) => {
            const target = ev.target;
            if (!target)
                return;
            // 1) массовое редактирование текста
            if (target.closest(".im-edit-text-all")) {
                this._onEditTextAll(root);
                return;
            }
            if (target.closest(".im-save-text-all")) {
                this._onSaveTextAll(root);
                return;
            }
            // 2) сохранить данные
            if (target.closest(".im-save-data")) {
                void this._buffSave();
                return;
            }
            // 3) change-status
            const chStatusBtn = target.closest(".im-change-status");
            if (chStatusBtn) {
                this._onChangeStatus(root, chStatusBtn);
                return;
            }
            // 4) кнопка "изменить название фильма"
            if (target.closest(".im-film-title-block-edit .btn")) {
                root.querySelectorAll(".im-film-title-block")
                    .forEach(el => el.style.removeProperty("display"));
                root.querySelectorAll(".im-film-title-block-edit")
                    .forEach(el => { el.style.display = "none"; });
                return;
            }
            // 5) кнопки формы привязки
            if (target.closest(".im-save-attach")) {
                this._saveAttachForm();
                return;
            }
            if (target.closest(".im-untie-attach")) {
                this._untieAttachForm();
                return;
            }
            // Дальше — кнопки внутри сцен (аналог _init_item)
            const scene = target.closest(".scenerange-collection");
            if (scene) {
                // если элемент задизейблен как в легаси
                if (this._isOff(target))
                    return;
                // join up
                if (target.closest(".im-join-up")) {
                    const prev = scene.previousElementSibling;
                    if (prev)
                        this._join(prev, scene);
                    return;
                }
                // join undo
                if (target.closest(".im-join-undo")) {
                    this._joinUndo(scene);
                    return;
                }
                // frame buttons
                if (target.closest(".im-bt-frame-in-left")) {
                    const prev = scene.previousElementSibling;
                    this._changeFrame(prev, scene, -1, ev.shiftKey);
                    return;
                }
                if (target.closest(".im-bt-frame-in-right")) {
                    const prev = scene.previousElementSibling;
                    this._changeFrame(prev, scene, 1, ev.shiftKey);
                    return;
                }
                if (target.closest(".im-bt-frame-out-left")) {
                    const next = scene.nextElementSibling;
                    this._changeFrame(scene, next, -1, ev.shiftKey);
                    return;
                }
                if (target.closest(".im-bt-frame-out-right")) {
                    const next = scene.nextElementSibling;
                    this._changeFrame(scene, next, 1, ev.shiftKey);
                    return;
                }
                // edit/save text per scene
                if (target.closest(".im-edit-text")) {
                    this._startEditText(scene);
                    return;
                }
                if (target.closest(".im-save-text")) {
                    this._endEditText(scene);
                    return;
                }
                // attach scene
                if (target.closest(".im-attach-scene")) {
                    this._showAttachForm(scene);
                    return;
                }
                // text up/down
                if (target.closest(".im-text-up")) {
                    const p = target.closest("p");
                    if (p)
                        this._textMoveUp(p);
                    return;
                }
                if (target.closest(".im-text-down")) {
                    const p = target.closest("p");
                    if (p)
                        this._textMoveDown(p);
                    return;
                }
            }
            // 6) проигрывание по клику на кадр (работает и если кликнули по img внутри .scenerange-item)
            const frameImg = target.closest(".scenerange-item img");
            if (frameImg) {
                const item = frameImg.closest(".scenerange-item");
                if (!item)
                    return;
                const inTK = item.getAttribute("data-framein")
                    ?? item.getAttribute("data-frame")
                    ?? item.getAttribute("data-frameout")
                    ?? "0";
                this._play(parseFloat(inTK));
            }
        });
        // change (делегирование через capture, чтобы ловить change от input/select)
        root.addEventListener("change", (ev) => {
            const target = ev.target;
            if (!target)
                return;
            // AttachmentID (верхний select в шапке/форме)
            const attach = target.closest('select[data-key="AttachmentID"]');
            if (attach && root.contains(attach)) {
                this._onAttachmentSelectChange(root, attach);
                return;
            }
            // SoundOn
            const sound = target.closest('[data-key="SoundOn"]');
            if (sound) {
                if (this._isOff(sound))
                    return;
                void this._buffSave();
                return;
            }
            // SceneType
            const sceneType = target.closest('[data-key="SceneType"]');
            if (sceneType) {
                if (this._isOff(sceneType))
                    return;
                const scene = sceneType.closest(".scenerange-collection");
                if (!scene)
                    return;
                this._onSceneTypeChange(scene, sceneType);
            }
        }, true);
    }
    /** Проверка: элемент/его родитель помечен как .off (как в легаси). */
    _isOff(el) {
        return !!el.closest(".off");
    }
    /** Массовое редактирование текста (как .im-edit-text-all). */
    _onEditTextAll(root) {
        root.querySelectorAll(".scenerange-collection").forEach(sc => this._startEditText(sc));
        root.querySelector(".im-edit-text-all")?.style.setProperty("display", "none");
        root.querySelector(".im-save-data")?.style.setProperty("display", "none");
        root.querySelector(".im-save-text-all")?.style.removeProperty("display");
    }
    /** Массовое сохранение текста (как .im-save-text-all). */
    _onSaveTextAll(root) {
        root.querySelectorAll(".scenerange-collection").forEach(sc => this._endEditText(sc));
        root.querySelector(".im-save-text-all")?.style.setProperty("display", "none");
        root.querySelector(".im-edit-text-all")?.style.removeProperty("display");
        root.querySelector(".im-save-data")?.style.removeProperty("display");
        void this._buffSave();
    }
    /** Обработчик смены AttachmentID (как attach.on('change') в легаси). */
    _onAttachmentSelectChange(root, attach) {
        const val = attach.value;
        if (val === "0") {
            const year = root.querySelector('[data-key="filmYear"]');
            const title = root.querySelector('[data-key="filmTitle"]');
            if (year)
                year.value = "";
            if (title)
                title.value = "";
            root.querySelectorAll(".im-film-title-block")
                .forEach(el => el.style.removeProperty("display"));
            root.querySelectorAll(".im-film-title-block-edit")
                .forEach(el => { el.style.display = "none"; });
        }
        else {
            const option = attach.querySelector(`option[value="${CSS.escape(val)}"]`);
            const year = root.querySelector('[data-key="filmYear"]');
            const title = root.querySelector('[data-key="filmTitle"]');
            if (option) {
                if (year)
                    year.value = option.dataset.year ?? "";
                if (title)
                    title.value = option.dataset.title ?? "";
            }
            root.querySelectorAll(".im-film-title-block")
                .forEach(el => { el.style.display = "none"; });
            root.querySelectorAll(".im-film-title-block-edit")
                .forEach(el => el.style.removeProperty("display"));
        }
    }
    /** change-status: сначала save через _execCMD, потом itemcmd clickitem от кнопки. */
    _onChangeStatus(root, btn) {
        this._execCMD("save", null, () => {
            // В легаси: self.element.imView('execCmd', btClicked, 'itemcmd', 'clickitem');
            ViewWidgetHelper.ExecCmd(btn, {
                cmdType: ViewCmdType.itemcmd,
                cmd: "clickitem"
            }, root);
        });
    }
    /** Перевод сцены в read-only (как блок в _create в легаси). */
    _applyReadOnlyState(root) {
        root.querySelectorAll(".im-read-only").forEach(ro => {
            if (ro.value !== "True")
                return;
            const sceneRange = ro.closest(".scenerange-collection");
            if (!sceneRange)
                return;
            // В легаси: off('click') + addClass('off')
            sceneRange
                .querySelectorAll(".im-join-up, .im-attach-scene, .im-edit-text, .im-bt-frame-in-left, .im-bt-frame-in-right, .im-bt-frame-out-left, .im-bt-frame-out-right, .im-text-down, .im-text-up")
                .forEach(x => x.classList.add("off"));
            // SoundOn: off(change) + next().addClass('off')
            const soundOn = sceneRange.querySelector('[data-key="SoundOn"]');
            soundOn?.nextElementSibling?.classList.add("off");
            // SceneType: off(change) + addClass('off')
            sceneRange.querySelector('[data-key="SceneType"]')?.classList.add("off");
            // sceneRange.next().find('.im-join-up').off('click').addClass('off');
            const nextBlock = sceneRange.nextElementSibling;
            nextBlock?.querySelectorAll(".im-join-up").forEach(x => x.classList.add("off"));
        });
    }
    /**
     * Обёртка для выполнения серверной команды (как _execCMD в легаси).
     * @param action Имя команды.
     * @param cmdOptions payload команды.
     * @param callback Успешный callback.
     * @param onerror Callback ошибки.
     * @param noProjectData Если true — не прикладывать project.
     */
    _execCMD(action, cmdOptions, callback, onerror, noProjectData) {
        const options = { action, options: cmdOptions ?? null };
        if (!noProjectData) {
            options.project = this._collectProjectData();
        }
        if (options.project == null) {
            ImSys.alert("Ошибка", "Ошибка сцен");
            return;
        }
        const btExec = this.element.querySelector('[data-key="btExecCmd"]') ?? this.element;
        ViewWidgetHelper.ExecCmd(btExec, {
            cmdType: ViewCmdType.itemcmd,
            cmd: "clickitem",
            options,
            onSuccess: callback,
            onError: onerror
        }, this.element);
    }
    /** Буферизованное сохранение (как _buffSave в легаси). */
    async _buffSave() {
        this._buffSaveRequired = true;
        if (this._buffSaveRunning)
            return;
        try {
            this._buffSaveRunning = true;
            while (this._buffSaveRequired) {
                this._buffSaveRequired = false;
                this._buffCmdExecuting = true;
                this._execCMD("save", { buffSave: true }, () => { this._buffCmdExecuting = false; }, () => { this._buffCmdExecuting = false; });
                let count = 0;
                while (this._buffCmdExecuting && count < 100) {
                    await this._sleep(100);
                    count++;
                }
                await this._sleep(500);
            }
        }
        catch (e) {
            console.error("_buffSave", e);
        }
        finally {
            this._buffSaveRunning = false;
        }
    }
    /** Sleep helper. */
    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /** Сбор данных проекта (как _collectProjectData в легаси). */
    _collectProjectData() {
        const root = this.element;
        // Attachments (films)
        const films = [];
        root.querySelectorAll('.im-details-form [data-key="AttachmentID"] option')
            .forEach(option => {
            if (option.value === "0")
                return;
            const it = {
                ID: option.value,
                Year: option.dataset.year,
                FilmTitle: option.dataset.title,
                Film: option.dataset.film
            };
            // В легаси тут был баг t.Film = null; но по смыслу Film должен стать null
            if (it.Film === '00000000-0000-0000-0000-000000000000' || it.Film === "") {
                it.Film = null;
            }
            films.push(it);
        });
        // Scenes
        const scenes = [];
        root.querySelectorAll(".scenerange-collection").forEach(range => {
            const txt = [];
            range.querySelectorAll(".im-text-place p").forEach(p => {
                const clone = p.cloneNode(true);
                clone.querySelector(".im-text-up")?.remove();
                clone.querySelector(".im-text-down")?.remove();
                txt.push((clone.textContent ?? "").trim());
            });
            const getVal = (sel) => (range.querySelector(sel)?.value ?? "");
            const getImVal = (sel) => getMainValue(range.querySelector(sel));
            scenes.push({
                FrameIn: +getVal('[data-key="FrameIn"]'),
                FrameOut: +getVal('[data-key="FrameOut"]'),
                FPS: this._fps,
                ID: getVal('[data-key="ID"]'),
                JoinedCount: getVal('[data-key="JoinedCount"]'),
                Texts: txt,
                AttachmentID: getVal('[data-key="AttachmentID"]'),
                PartNum: getImVal('[data-key="PartNum"]'),
                PartTitle: getVal('[data-key="PartTitle"]'),
                KeyWords: getImVal('[data-key="KeyWords"]'),
                PersonnelsText: getImVal('[data-key="PersonnelsText"]'),
                Calendar: getImVal('[data-key="Calendar"]'),
                Locations: getImVal('[data-key="Locations"]'),
                Objects: getImVal('[data-key="Objects"]'),
                Topics: getImVal('[data-key="Topics"]'),
                SoundOn: !!range.querySelector('[data-key="SoundOn"]')?.checked,
                SceneType: getVal('[data-key="SceneType"]'),
                OrdersCount: getVal('[data-key="OrdersCount"]'),
                OrdersSeconds: getVal('[data-key="OrdersSeconds"]'),
                PartUI: getVal('[data-key="PartUI"]')
            });
        });
        // Проверить порядок сцен (как в легаси)
        let nextFrame = 1;
        let hasErr = false;
        for (let i = 0; i < scenes.length; i++) {
            if (scenes[i].FrameIn !== nextFrame) {
                hasErr = true;
                break;
            }
            nextFrame = scenes[i].FrameOut + 1;
        }
        if (hasErr)
            return null;
        return {
            Scenes: scenes,
            Attachments: films,
            Description: root.querySelector('[data-key="Description"]')?.value ?? ""
        };
    }
    /** Открытие формы связывания с сюжетом (как _showAttachForm). */
    _showAttachForm(el) {
        const form = this.element.querySelector(".im-modal-aside.im-details-form");
        if (!form)
            return;
        DomHelpers.show(form);
        const untie = form.querySelector(".im-untie-attach");
        if (untie) {
            if (el.classList.contains("film-attached")) {
                DomHelpers.show(untie);
            }
            else {
                DomHelpers.hide(untie);
            }
        }
        // Убираем дубликаты AttachmentID (как в легаси)
        const arrID = [];
        form.querySelectorAll('[data-key="AttachmentID"] option')
            .forEach(op => arrID.push(+op.value));
        const duplicatesArr = arrID.filter((item, index) => arrID.indexOf(item) !== index);
        for (let i = 0; i < duplicatesArr.length; i++) {
            const dupe = duplicatesArr[i];
            const list = form.querySelectorAll(`[data-key="AttachmentID"] option[value="${dupe}"]`);
            if (list.length > 0)
                list[0].remove();
        }
        const wasID = el.querySelector('[data-key="AttachmentID"]')?.value ?? "0";
        const sel = form.querySelector('[data-key="AttachmentID"]');
        if (sel) {
            sel.value = wasID;
            sel.dispatchEvent(new Event("change", { bubbles: true }));
        }
        const keys = ["PartTitle", "KeyWords", "PersonnelsText", "Calendar", "Locations", "Objects", "Topics"];
        const appVals = {};
        let keysStr = "";
        for (const key of keys) {
            keysStr += (keysStr === "" ? "" : ",") + key;
            const mask = `[data-key="${key}"]`;
            appVals[key] = getMainValue(el.querySelector(mask));
        }
        // Собираем SceneDescription из <p> (как в легаси)
        let text = "";
        el.querySelectorAll(".im-text-place p").forEach(p => {
            const clone = p.cloneNode(true);
            clone.querySelector(".im-text-up")?.remove();
            clone.querySelector(".im-text-down")?.remove();
            const t = (clone.textContent ?? "").trim();
            text += (text ? "\n" : "") + t;
        });
        const desc = form.querySelector('[data-key="SceneDescription"]');
        if (desc)
            desc.value = text;
        this._attachedElement = el;
        const innerView = form.querySelector(".im-view");
        if (innerView) {
            // Внутри reload используется jQuery-совместимый слой
            ViewWidgetHelper.reload(innerView, {
                itemKeysToReload: keysStr,
                onCallback: () => {
                    form.style.removeProperty("display");
                    form.querySelector(".autoresize")
                        ?.dispatchEvent(new CustomEvent("updateAutoresize", { bubbles: true }));
                },
                reloadType: ViewCmdType.reload,
                options: appVals
            });
        }
        else {
            form.style.removeProperty("display");
        }
    }
    /** Сохранение созданного сюжета (как _saveAttachForm). */
    _saveAttachForm() {
        const proj = this._collectProjectData();
        if (proj == null) {
            ImSys.alert("Ошибка", "Ошибка сцен");
            return;
        }
        const el = this._attachedElement;
        const form = this.element.querySelector(".im-modal-aside.im-details-form");
        if (!el || !form)
            return;
        const select = form.querySelector('[data-key="AttachmentID"]');
        const year = getMainValue(form.querySelector('[data-key="filmYear"]'));
        const title = (form.querySelector('[data-key="filmTitle"]')?.value ?? "");
        if (year === "" || title === "") {
            ImSys.alert("Ошибка", "Не указан год или название фильма");
            return;
        }
        const partTitle = (form.querySelector('[data-key="PartTitle"]')?.value ?? "");
        if (!select)
            return;
        let nowID = select.value;
        if (nowID === "0") {
            // Следующий id -> количество option (как в легаси)
            const max = select.querySelectorAll("option").length;
            const op = document.createElement("option");
            op.value = String(max);
            op.dataset.year = String(year);
            op.dataset.title = title;
            op.dataset.film = "";
            op.textContent = `${title} (${year})`;
            select.append(op);
            select.value = String(max);
            nowID = String(max);
        }
        else {
            const op = select.querySelector(`option[value="${CSS.escape(nowID)}"]`);
            if (op) {
                op.dataset.year = String(year);
                op.dataset.title = title;
                op.textContent = `${title} (${year})`;
            }
        }
        const keys = ["AttachmentID", "PartTitle", "KeyWords", "PersonnelsText", "Calendar", "Locations", "Objects", "Topics"];
        for (const key of keys) {
            const mask = `[data-key="${key}"]`;
            const val = getMainValue(form.querySelector(mask));
            setMainValue(el.querySelector(mask), val);
        }
        el.classList.add("film-attached");
        const info = el.querySelector(".im-film-info span");
        const nowOpt = select.querySelector(`option[value="${CSS.escape(nowID)}"]`);
        if (info && nowOpt)
            info.textContent = `${nowOpt.textContent ?? ""} ${partTitle}`;
        // Пересобираем тексты (как в легаси)
        const place = el.querySelector(".im-text-place");
        if (place) {
            place.textContent = "";
            const text = form.querySelector('[data-key="SceneDescription"]')?.value ?? "";
            if (text !== "") {
                text.split("\n").forEach(line => {
                    const txt = line.trim();
                    if (txt === "")
                        return;
                    const p = document.createElement("p");
                    p.textContent = txt;
                    const up = utils.createSvg("arrow_t8-up", "im-text-up mr8");
                    const down = utils.createSvg("arrow_t8-down", "im-text-down");
                    if (up)
                        p.insertAdjacentHTML("afterbegin", up);
                    if (down)
                        p.insertAdjacentHTML("afterbegin", down);
                    place.append(p);
                });
            }
        }
        el.querySelector(".im-film-info")?.style.removeProperty("display");
        form.querySelector(".modal-aside__close")
            ?.dispatchEvent(new Event("click", { bubbles: true }));
        void this._buffSave();
    }
    /** Отвязка сюжета (как _untieAttachForm). */
    _untieAttachForm() {
        const el = this._attachedElement;
        const form = this.element.querySelector(".im-modal-aside.im-details-form");
        if (!el || !form)
            return;
        const partUI = el.querySelector('[data-key="PartUI"]')?.value;
        if (partUI != null && partUI !== "" && partUI !== '00000000-0000-0000-0000-000000000000') {
            ImSys.alert("Ошибка", "Невозможно отвязать сюжет");
            return;
        }
        const keys = ["AttachmentID", "PartTitle", "PartNum", "KeyWords", "PersonnelsText", "Calendar", "Locations", "Objects", "Topics"];
        for (const key of keys) {
            const mask = `[data-key="${key}"]`;
            const target = el.querySelector(mask);
            setMainValue(target, '');
            // для фильма и номера части передается значение 0
            if (key === "AttachmentID" || key === "PartNum") {
                setMainValue(target, 0);
            }
        }
        // Пересобираем текст из поля формы (как в легаси)
        const place = el.querySelector(".im-text-place");
        if (place) {
            place.textContent = "";
            const text = form.querySelector('[data-key="SceneDescription"]')?.value ?? "";
            if (text !== "") {
                text.split("\n").forEach(line => {
                    const txt = line.trim();
                    if (txt === "")
                        return;
                    const p = document.createElement("p");
                    p.textContent = txt;
                    const up = utils.createSvg("arrow_t8-up", "im-text-up mr8");
                    const down = utils.createSvg("arrow_t8-down", "im-text-down");
                    if (up)
                        p.insertAdjacentHTML("afterbegin", up);
                    if (down)
                        p.insertAdjacentHTML("afterbegin", down);
                    place.append(p);
                });
            }
        }
        el.classList.remove("film-attached");
        const span = el.querySelector(".im-film-info span");
        if (span)
            span.textContent = "";
        const info = el.querySelector(".im-film-info");
        if (info)
            info.style.display = "none";
        form.querySelector(".modal-aside__close")
            ?.dispatchEvent(new Event("click", { bubbles: true }));
        void this._buffSave();
    }
    /** join-undo (как обработчик .im-join-undo в легаси). */
    _joinUndo(cur) {
        const proj = this._collectProjectData();
        if (proj == null) {
            ImSys.alert("Ошибка", "Ошибка сцен");
            return;
        }
        const id = (cur.querySelector('[data-key="ID"]')?.value ?? "");
        const attachmentId = parseInt(cur.querySelector('[data-key="AttachmentID"]')?.value ?? "0", 10);
        const doCall = () => {
            this._execCMD("join-undo", { ID: id }, (data) => {
                let prevItem = cur;
                const items = data?.items ?? {};
                for (const key of Object.keys(items)) {
                    const itemHtml = items[key];
                    const item = this._htmlToElement(itemHtml);
                    prevItem.after(item);
                    this._removeEmptyTextRows(item);
                    ImSys.initDom(item);
                    prevItem = item;
                }
                cur.remove();
            });
        };
        if (attachmentId > 0) {
            ImSys.alertConfirm("Данные сцены объединены в сюжет", "Подтвердите разделение сцен", doCall);
        }
        else {
            doCall();
        }
    }
    /** Начать редактирование текста (как _startEditText). */
    _startEditText(el) {
        const place = el.querySelector(".im-text-place");
        if (!place)
            return;
        let txt = "";
        place.querySelectorAll("p").forEach(p => {
            p.querySelector(".im-text-up")?.remove();
            p.querySelector(".im-text-down")?.remove();
            const line = (p.textContent ?? "").trim();
            if (line !== "") {
                txt += (txt === "" ? "" : "\n") + line;
            }
            p.remove();
        });
        const textarea = document.createElement("textarea");
        textarea.className = "w600";
        textarea.rows = 10;
        textarea.value = txt;
        const p2 = document.createElement("p");
        p2.append(textarea);
        place.append(p2);
        el.querySelector(".im-edit-text")?.style.setProperty("display", "none");
        el.querySelector(".im-save-text")?.style.removeProperty("display");
    }
    /** Завершить редактирование текста (как _endEditText). */
    _endEditText(el) {
        const place = el.querySelector(".im-text-place");
        if (!place)
            return;
        const textarea = place.querySelector("textarea");
        const text = textarea?.value ?? "";
        textarea?.remove();
        if (text !== "") {
            text.split("\n").forEach(line => {
                const txt = line.trim();
                if (txt === "")
                    return;
                const p = document.createElement("p");
                p.textContent = txt;
                const up = utils.createSvg("arrow_t8-up", "im-text-up mr8");
                const down = utils.createSvg("arrow_t8-down", "im-text-down");
                if (up)
                    p.insertAdjacentHTML("afterbegin", up);
                if (down)
                    p.insertAdjacentHTML("afterbegin", down);
                place.append(p);
            });
        }
        el.querySelector(".im-edit-text")?.style.removeProperty("display");
        el.querySelector(".im-save-text")?.style.setProperty("display", "none");
        void this._buffSave();
    }
    /** Сдвиг границ кадров (как _changeFrame в легаси). */
    _changeFrame(el1, el2, decFrame, shift) {
        const proj = this._collectProjectData();
        if (proj == null) {
            ImSys.alert("Ошибка", "Ошибка сцен");
            return;
        }
        // Если сюжет создан, то невозможно сдвинуть кадры (как в легаси: read-only)
        const ro1 = el1?.querySelector(".im-read-only")?.value === "True";
        const ro2 = el2?.querySelector(".im-read-only")?.value === "True";
        if (ro1 || ro2)
            return;
        // У первой сцены FrameIn должен оставаться 1, иначе _collectProjectData вернёт ошибку сцен.
        if (!el1 && el2)
            return;
        // У последней сцены FrameOut должен оставаться равным общему числу кадров.
        if (el1 && !el2)
            return;
        if (shift)
            decFrame *= this._fps;
        let valNew;
        if (!el1) {
            // Самый 1й фрейм
            const input1 = el2?.querySelector('[data-key="FrameIn"]');
            if (!input1)
                return;
            valNew = parseInt(input1.value, 10) + decFrame;
        }
        else if (!el2) {
            // Самый последний фрейм
            const input1 = el1.querySelector('[data-key="FrameOut"]');
            if (!input1)
                return;
            valNew = parseInt(input1.value, 10) + decFrame;
        }
        else {
            const input1 = el1.querySelector('[data-key="FrameOut"]');
            if (!input1)
                return;
            valNew = parseInt(input1.value, 10) + decFrame;
        }
        if (el1) {
            const frameIn = el1.querySelector('[data-key="FrameIn"]');
            if (frameIn && valNew <= +frameIn.value) {
                valNew = +frameIn.value + 1;
            }
        }
        if (el2) {
            const frameOut = el2.querySelector('[data-key="FrameOut"]');
            if (frameOut && valNew >= +frameOut.value) {
                valNew = +frameOut.value - 2;
            }
        }
        if (valNew < 1)
            valNew = 1;
        if (valNew > this._totalFrames)
            valNew = this._totalFrames;
        if (!el1 && el2) {
            const out2 = el2.querySelector('[data-key="FrameOut"]');
            if (!out2)
                return;
            this._setInOut(el2, valNew, +out2.value);
        }
        else if (!el2 && el1) {
            const in1 = el1.querySelector('[data-key="FrameIn"]');
            if (!in1)
                return;
            this._setInOut(el1, +in1.value, valNew);
        }
        else if (el1 && el2) {
            const in1 = el1.querySelector('[data-key="FrameIn"]');
            const out2 = el2.querySelector('[data-key="FrameOut"]');
            if (!in1 || !out2)
                return;
            this._setInOut(el1, +in1.value, valNew);
            this._setInOut(el2, valNew + 1, +out2.value);
        }
        void this._buffSave();
    }
    /** Проигрывание (как _play в легаси). */
    _play(frame) {
        const timeIn = frame / this._fps;
        const nfPlayer = document.querySelector(".nf-player");
        let player;
        if (nfPlayer) {
            const video = nfPlayer.querySelector("video");
            if (video && !video.getAttribute("src")) {
                const modalVideo = this.element.querySelector(".modal-aside_player video");
                if (modalVideo)
                    video.setAttribute("src", modalVideo.getAttribute("src") ?? "");
            }
            nfPlayer.style.removeProperty("display");
            nfPlayer.classList.remove("size-1");
            nfPlayer.classList.add("size-2");
            player = Widget.getOnByName(nfPlayer, "nfPlayer");
        }
        else {
            const modal = this.element.querySelector(".modal-aside_player");
            const playEl = modal?.querySelector(".im-vs-player");
            if (modal)
                modal.style.removeProperty("display");
            if (playEl) {
                player = Widget.getOnByName(playEl, "VSPlayer");
            }
        }
        if (player) {
            player.seek(timeIn);
            player.play();
        }
    }
    /** Перенос текста вверх (как _textMoveUp в легаси). */
    _textMoveUp(pEl) {
        const cur = pEl.closest(".scenerange-collection");
        const prev = cur?.previousElementSibling;
        if (!prev)
            return;
        // Не переносить текст если сюжет создан у предыдущей сцены
        if (prev.querySelector(".im-read-only")?.value === "True")
            return;
        const proj = this._collectProjectData();
        if (proj == null) {
            ImSys.alert("Ошибка", "Ошибка сцен");
            return;
        }
        // jQuery: prevAll().addBack() => [prevPrev, prev, current]
        const items = [...this._prevAll(pEl), pEl];
        const place = prev.querySelector(".im-text-place");
        if (!place)
            return;
        const frag = document.createDocumentFragment();
        for (const n of items)
            frag.appendChild(n);
        place.appendChild(frag);
        void this._buffSave();
    }
    /** Перенос текста вниз (как _textMoveDown в легаси). */
    _textMoveDown(pEl) {
        const cur = pEl.closest(".scenerange-collection");
        const next = cur?.nextElementSibling;
        if (!next)
            return;
        // Не переносить текст если сюжет создан у следующей сцены
        if (next.querySelector(".im-read-only")?.value === "True")
            return;
        const proj = this._collectProjectData();
        if (proj == null) {
            ImSys.alert("Ошибка", "Ошибка сцен");
            return;
        }
        // jQuery: nextAll().addBack() => [current, next, nextNext]
        const items = [pEl, ...this._nextAll(pEl)];
        const place = next.querySelector(".im-text-place");
        if (!place)
            return;
        const frag = document.createDocumentFragment();
        for (const n of items)
            frag.appendChild(n);
        // prepend фрагмента сохраняет порядок
        place.prepend(frag);
        void this._buffSave();
    }
    /** Объединение 2х сюжетов (как _join в легаси). */
    _join(el1, el2, confirmed) {
        const proj = this._collectProjectData();
        if (proj == null) {
            ImSys.alert("Ошибка", "Ошибка сцен");
            return;
        }
        const a1 = parseInt(el1.querySelector('[data-key="AttachmentID"]')?.value ?? "0", 10);
        const a2 = parseInt(el2.querySelector('[data-key="AttachmentID"]')?.value ?? "0", 10);
        if (!confirmed) {
            if (a1 > 0 && a2 > 0) {
                ImSys.alert("Ошибка", "Объединение невозможно. Обе сцены содержат сюжеты");
                return;
            }
            if (a1 > 0 || a2 > 0) {
                ImSys.alertConfirm("Объединяемые сцены содержат созданные сюжеты", "Подтвердите объединение", () => this._join(el1, el2, true));
                return;
            }
        }
        const fIn = parseInt(el1.querySelector('[data-key="FrameIn"]')?.value ?? "0", 10);
        const fOut = parseInt(el2.querySelector('[data-key="FrameOut"]')?.value ?? "0", 10);
        const joinedCount = parseInt(el1.querySelector('[data-key="JoinedCount"]')?.value ?? "0", 10) +
            parseInt(el2.querySelector('[data-key="JoinedCount"]')?.value ?? "0", 10);
        const ordersCount = parseInt(el1.querySelector('[data-key="OrdersCount"]')?.value ?? "0", 10) +
            parseInt(el2.querySelector('[data-key="OrdersCount"]')?.value ?? "0", 10);
        const ordersSeconds = parseInt(el1.querySelector('[data-key="OrdersSeconds"]')?.value ?? "0", 10) +
            parseInt(el2.querySelector('[data-key="OrdersSeconds"]')?.value ?? "0", 10);
        this._setInOut(el1, fIn, fOut);
        const jc = el1.querySelector('[data-key="JoinedCount"]');
        const oc = el1.querySelector('[data-key="OrdersCount"]');
        const os = el1.querySelector('[data-key="OrdersSeconds"]');
        if (jc)
            jc.value = String(joinedCount);
        if (oc)
            oc.value = String(ordersCount);
        if (os)
            os.value = String(ordersSeconds);
        const ordersHit = el1.querySelector(".im-orders-hit");
        if (ordersHit) {
            if (ordersCount > 0) {
                ordersHit.style.removeProperty("display");
                const svg = utils.createSvg("dollar");
                ordersHit.innerHTML = (typeof svg === "string" ? svg : "") + String(ordersCount);
            }
            else {
                ordersHit.style.display = "none";
            }
        }
        el1.querySelector(".im-join-undo")?.style.removeProperty("display");
        const joinCnt = el1.querySelector(".im-join-count");
        if (joinCnt) {
            joinCnt.textContent = String(joinedCount);
            joinCnt.style.removeProperty("display");
        }
        // Перенос текстов (как в легаси: append(html))
        const place1 = el1.querySelector(".im-text-place");
        const place2 = el2.querySelector(".im-text-place");
        if (place1 && place2) {
            place1.insertAdjacentHTML("beforeend", place2.innerHTML);
            this._removeEmptyTextRows(el1);
        }
        el2.remove();
        void this._buffSave();
    }
    /** Удалить пустые строки текста в сцене (p без текстового контента). */
    _removeEmptyTextRows(scene) {
        scene.querySelectorAll(".im-text-place p").forEach(p => {
            const clone = p.cloneNode(true);
            clone.querySelector(".im-text-up")?.remove();
            clone.querySelector(".im-text-down")?.remove();
            if ((clone.textContent ?? "").trim() === "") {
                p.remove();
            }
        });
    }
    /** SceneType change (как в легаси). */
    _onSceneTypeChange(scene, sel) {
        const attachmentId = parseInt(scene.querySelector('[data-key="AttachmentID"]')?.value ?? "0", 10);
        if (attachmentId > 0 && sel.value !== "0") {
            ImSys.alert("Ошибка", "У сцены уже есть созданный сюжет");
            sel.value = "0";
            return;
        }
        const wasIgnored = scene.querySelector('[data-key="IsIgnored"]')?.value == "True";
        const newVal = getMainValue(sel);
        if ((wasIgnored && newVal == 0) || (!wasIgnored && newVal != 0)) {
            this._execCMD("reload-scene", {
                ID: scene.querySelector('[data-key="ID"]')?.value,
                SceneType: newVal
            }, (data) => {
                const itemHtml = data?.itemHtml;
                if (!itemHtml)
                    return;
                const item = this._htmlToElement(itemHtml);
                scene.after(item);
                ImSys.initDom(item);
                scene.remove();
            }, undefined, false);
        }
        else {
            void this._buffSave();
        }
    }
    /** Обновление таймкодов/превью кадров (как _setInOut в легаси). */
    _setInOut(el, fIn, fOut) {
        fIn = parseFloat(String(fIn));
        fOut = parseFloat(String(fOut));
        const durationStr = window.imsys?.getTimecodeStr?.((fOut - fIn) / this._fps, this._fps) ?? "";
        const dur = el.querySelector(".im-result-duration");
        if (dur)
            dur.innerHTML = durationStr;
        const fin = el.querySelector('[data-key="FrameIn"]');
        const fout = el.querySelector('[data-key="FrameOut"]');
        if (fin)
            fin.value = String(fIn);
        if (fout)
            fout.value = String(fOut);
        // В легаси base-url брался из первого img внутри сцены
        const anyImg = el.querySelector("img");
        const src = anyImg?.getAttribute("src") ?? "";
        const base = src ? src.substring(0, src.lastIndexOf("/") + 1) : "";
        const fr1 = el.querySelector(".f1");
        if (fr1) {
            fr1.dataset.framein = String(fIn);
            fr1.querySelector("img")?.setAttribute("src", base + String(fIn));
        }
        const fr2 = el.querySelector(".f2");
        if (fr2) {
            const v = Math.round(fIn + ((fOut - fIn) * 1 / 4));
            fr2.dataset.frame = String(v);
            fr2.querySelector("img")?.setAttribute("src", base + String(v));
        }
        const fr3 = el.querySelector(".f3");
        if (fr3) {
            const v = Math.round(fIn + ((fOut - fIn) * 2 / 4));
            fr3.dataset.frame = String(v);
            fr3.querySelector("img")?.setAttribute("src", base + String(v));
        }
        const fr4 = el.querySelector(".f4");
        if (fr4) {
            const v = Math.round(fIn + ((fOut - fIn) * 3 / 4));
            fr4.dataset.frame = String(v);
            fr4.querySelector("img")?.setAttribute("src", base + String(v));
        }
        const fr5 = el.querySelector(".f5");
        if (fr5) {
            fr5.dataset.frameout = String(fOut);
            fr5.querySelector("img")?.setAttribute("src", base + String(fOut));
        }
    }
    /** Собрать prevAll (как jQuery prevAll) в порядке DOM. */
    _prevAll(p) {
        const list = [];
        let n = p.previousElementSibling;
        while (n) {
            list.unshift(n);
            n = n.previousElementSibling;
        }
        return list;
    }
    /** Собрать nextAll (как jQuery nextAll) в порядке DOM. */
    _nextAll(p) {
        const list = [];
        let n = p.nextElementSibling;
        while (n) {
            list.push(n);
            n = n.nextElementSibling;
        }
        return list;
    }
    /** Превратить HTML строки в HTMLElement. */
    _htmlToElement(html) {
        const tpl = document.createElement("template");
        tpl.innerHTML = html.trim();
        return tpl.content.firstElementChild;
    }
    /** Вызывается родителем (Widget) при уничтожении. */
    _onDestroy() { }
}
NFSplitProjectWidget._alias = "NfSplitProject";

export { NFSplitProjectWidget };
